import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { Mail, Phone, MapPin, Instagram, Facebook, Lock } from 'lucide-react';

const SOCIALS = [
  { id: 'instagram', Icon: Instagram, href: 'https://www.instagram.com/iaccounting.uae', label: 'Instagram' },
  { id: 'facebook', Icon: Facebook, href: 'https://www.facebook.com/share/1H2u5AQAGx/', label: 'Facebook' },
];
import { Input } from './ui/input';
import { Button } from './ui/button';
import { subscribeNewsletter } from '../api';
import { toast } from '../hooks/use-toast';
import { VisaLogo, MastercardLogo, AmexLogo } from './PaymentLogos';

const Footer = () => {
  const { t, dir, lang } = useLang();
  const [email, setEmail] = useState('');
  const [subbing, setSubbing] = useState(false);

  const handleSub = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubbing(true);
    try {
      await subscribeNewsletter(email);
      toast({ title: lang === 'ar' ? 'تم الاشتراك بنجاح' : 'Subscribed successfully' });
      setEmail('');
    } catch {
      toast({ title: lang === 'ar' ? 'حدث خطأ' : 'Error subscribing', variant: 'destructive' });
    } finally {
      setSubbing(false);
    }
  };
  return (
    <footer className="relative border-t border-[#e5e7eb] bg-[#f1f5f9] mt-24" dir={dir}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] flex items-center justify-center">
              <span className="text-[#f8fafc] font-display text-xl font-bold">i</span>
            </div>
            <div>
              <div className="font-display text-lg text-[#0a0a0a]">{t('brand')}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#2563eb]/70">{t('brand_sub')}</div>
            </div>
          </div>
          <p className="text-sm text-[#111827]/60 max-w-md leading-relaxed">
            {t('footer.tagline')}{' '}
            <span className="inline-block ms-1 text-[#2563eb] font-semibold" data-testid="license-no">
              {lang === 'ar' ? 'رقم الرخصة: 1268198' : 'License No: 1268198'}
            </span>
          </p>
          <div className="flex items-center gap-3 mt-6">
            {SOCIALS.map(({ id, Icon, href, label }) => (
              <a key={id} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} data-testid={`social-${id}`} className="w-10 h-10 rounded-full border border-[#2563eb]/25 flex items-center justify-center text-[#2563eb] hover:bg-[#2563eb]/10 transition-colors">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#2563eb] mb-4">{t('footer.quick')}</div>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/Services" className="text-[#111827]/70 hover:text-[#3b82f6] transition-colors">{t('nav.services')}</Link></li>
            <li><Link to="/Pricing" className="text-[#111827]/70 hover:text-[#3b82f6] transition-colors">{t('nav.pricing')}</Link></li>
            <li><Link to="/Comparison" className="text-[#111827]/70 hover:text-[#3b82f6] transition-colors">{t('nav.compare')}</Link></li>
            <li><Link to="/Calculator" className="text-[#111827]/70 hover:text-[#3b82f6] transition-colors">{t('nav.calculator')}</Link></li>
            <li><Link to="/About" className="text-[#111827]/70 hover:text-[#3b82f6] transition-colors">{t('nav.about')}</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#2563eb] mb-4">{t('footer.newsletter')}</div>
          <p className="text-sm text-[#111827]/60 mb-3">{t('footer.newsletter_sub')}</p>
          <form onSubmit={handleSub} className="flex gap-2">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('footer.email_ph')} className="bg-[#ffffff] border-[#2563eb]/25 text-[#111827] rounded-md h-10 focus-visible:ring-[#2563eb]/40" />
            <Button type="submit" disabled={subbing} className="gold-button rounded-md h-10 px-4 text-xs">{subbing ? '...' : t('footer.subscribe')}</Button>
          </form>
          <div className="mt-6 space-y-2 text-xs text-[#111827]/50">
            <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#2563eb]" /> info@iaccounting.ae</div>
            <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#2563eb]" /> +971 55 109 6418</div>
            <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#2563eb]" /> Business Bay, Dubai, UAE</div>
          </div>
        </div>
      </div>
      <div className="border-t border-[#2563eb]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#2563eb]/80">{t('payment.title')}</div>
            <div className="flex items-center gap-2.5">
              <div className="bg-white rounded-md px-3 py-1.5 flex items-center justify-center h-9 w-14 shadow-sm">
                <VisaLogo className="h-4 w-auto" />
              </div>
              <div className="bg-white rounded-md px-3 py-1.5 flex items-center justify-center h-9 w-14 shadow-sm">
                <MastercardLogo className="h-6 w-auto" />
              </div>
              <div className="bg-white rounded-md px-1.5 py-1.5 flex items-center justify-center h-9 w-14 shadow-sm">
                <AmexLogo className="h-6 w-auto" />
              </div>
            </div>
            <div className="hidden md:flex items-center gap-1.5 text-[10px] text-[#111827]/50">
              <Lock className="w-3 h-3 text-blue-400/70" />
              <span>{t('payment.secure')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[#2563eb]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs text-[#111827]/40">© 2023 {t('brand')}. {t('footer.rights')}</div>
          <div className="flex items-center gap-6 text-xs text-[#111827]/50">
            <Link to="/" className="hover:text-[#3b82f6]">{t('footer.privacy')}</Link>
            <Link to="/terms" className="hover:text-[#3b82f6]" data-testid="footer-terms-link">{t('footer.terms')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
